import { Col, Form, Input, message, Modal, Row, Tabs } from "antd";
import { Rule } from "antd/lib/form";
import { customerApi } from "api/customer.api";
import { staffApi } from "api/staff.api";
import { AppButton, AppSelect } from "components/antd-ui";
import { SingleImageUpload } from "components/Upload/SingleImageUpload";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { userStore } from "store/userStore";
import { Customer } from "types/customer";
import { ModalStatus } from "types/modal";
import { Staff } from "types/staff";
import { can } from "utils/auth";
import { $url } from "utils/url";
import "../Customer.scss";
import { CustomerDebtTab } from "./CustomerDebtTab";
import { CustomerOpportunityTab } from "./CustomerOpportunityTab";
import { CustomerOrderTab } from "./CustomerOrderTab";
import { CustomerQuoteTab } from "./CustomerQuoteTab";

const customerPath = "/customer";
const uploadUrl = import.meta.env.VITE_API_URL + "/v1/admin/staff/upload";
const nameRules: Rule[] = [{ required: true, message: "Bắt buộc" }];
const staffRules: Rule[] = [{ required: true, message: "Bắt buộc" }];
const emailRules: Rule[] = [{ type: "email", message: "Email không hợp lệ" }];

type StaffOption = { label: string; value: number };

interface CustomerForm {
  name: string;
  code?: string;
  taxCode?: string;
  address?: string;
  staffId?: number;
  contactName1?: string;
  contactPhone1?: string;
  contactEmail1?: string;
  contactAvatar1?: string;
  contactPosition1?: string;
  contactName2?: string;
  contactPhone2?: string;
  contactEmail2?: string;
  contactAvatar2?: string;
  contactPosition2?: string;
}

export interface CustomerModal {
  handleCreate: () => void;
  handleUpdate: (customer: Customer) => void;
  handleView: (customer: Customer) => void;
}

interface CustomerModalProps {
  onClose: () => void;
  onSubmitOk: (customer?: Customer) => void;
}

const display = (value?: string) => value?.trim() || "--";

const toForm = (customer: Customer): CustomerForm => ({
  name: customer.name,
  code: customer.code,
  taxCode: customer.taxCode,
  address: customer.address,
  staffId: customer.staff?.id,
  contactName1: customer.contactName1,
  contactPhone1: customer.contactPhone1,
  contactEmail1: customer.contactEmail1,
  contactAvatar1: customer.contactAvatar1,
  contactPosition1: customer.contactPosition1,
  contactName2: customer.contactName2,
  contactPhone2: customer.contactPhone2,
  contactEmail2: customer.contactEmail2,
  contactAvatar2: customer.contactAvatar2,
  contactPosition2: customer.contactPosition2,
});

const CustomerDetail = ({ customer }: { customer: Customer }) => {
  const contacts = [
    {
      title: "Người liên hệ 1",
      name: customer.contactName1,
      phone: customer.contactPhone1,
      email: customer.contactEmail1,
      avatar: customer.contactAvatar1,
      position: customer.contactPosition1,
    },
    {
      title: "Người liên hệ 2",
      name: customer.contactName2,
      phone: customer.contactPhone2,
      email: customer.contactEmail2,
      avatar: customer.contactAvatar2,
      position: customer.contactPosition2,
    },
  ];

  return (
    <div>
      <div className="customerModal__section">
        <div className="customerModal__title">Thông tin khách hàng</div>
        <div className="customerModal__grid">
          <span className="customerModal__label">Tên</span>
          <span>{display(customer.name)}</span>
          <span className="customerModal__label">Mã</span>
          <span>{display(customer.code)}</span>
          <span className="customerModal__label">Mã số thuế</span>
          <span>{display(customer.taxCode)}</span>
          <span className="customerModal__label">Địa chỉ</span>
          <span>{display(customer.address)}</span>
          <span className="customerModal__label">Sale chăm sóc</span>
          <span>{display(customer.staff?.name)}</span>
        </div>
      </div>

      <div className="customerModal__contacts">
        {contacts.map((contact) => (
          <div key={contact.title} className="customerModal__section">
            <div className="customerModal__title">{contact.title}</div>
            <div className="customerModal__contact">
              {contact.avatar ? (
                <img className="customerModal__avatar" src={$url(contact.avatar)} alt="" />
              ) : (
                <div className="customerModal__avatar customerModal__avatar--empty">--</div>
              )}
              <div className="customerModal__grid">
                <span className="customerModal__label">Tên</span>
                <span>{display(contact.name)}</span>
                <span className="customerModal__label">Số điện thoại</span>
                <span>{display(contact.phone)}</span>
                <span className="customerModal__label">Email</span>
                <span>{display(contact.email)}</span>
                <span className="customerModal__label">Chức vụ</span>
                <span>{display(contact.position)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const CustomerModal = observer(
  React.forwardRef(({ onClose, onSubmitOk }: CustomerModalProps, ref) => {
    const [form] = Form.useForm<CustomerForm>();
    const [loading, setLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const [status, setStatus] = useState<ModalStatus>("create");
    const [selectedCustomer, setSelectedCustomer] = useState<Customer>();
    const [staffOptions, setStaffOptions] = useState<StaffOption[]>([]);
    const [activeTab, setActiveTab] = useState("info");

    const onlyMine = !userStore.info.role?.isAdmin && can(customerPath, "ONLY_MINE");
    const canUpdate = can(customerPath, "UPDATE");

    const fetchStaff = useCallback(
      async (keyword: string) => {
        const res = await staffApi.findAll({ page: 1, limit: 20, search: keyword });
        const staffs: Staff[] = res.data.staffs || [];
        setStaffOptions((prev) => {
          const selectedId = form.getFieldValue("staffId");
          const selected = prev.find((option) => option.value === selectedId);
          const options = staffs.map((staff) => ({
            label: staff.name || staff.username,
            value: staff.id,
          }));
          if (selected && !options.some((option) => option.value === selected.value)) {
            return [selected, ...options];
          }
          return options;
        });
      },
      [form],
    );

    const debounceSearchStaff = useMemo(
      () =>
        debounce((keyword: string) => {
          fetchStaff(keyword);
        }, 500),
      [fetchStaff],
    );

    useEffect(() => {
      return () => {
        debounceSearchStaff.cancel();
      };
    }, [debounceSearchStaff]);

    useEffect(() => {
      if (!visible || onlyMine || status === "view") {
        return;
      }
      fetchStaff("");
    }, [visible, onlyMine, status, fetchStaff]);

    const fillCustomer = useCallback(
      (customer: Customer) => {
        form.setFieldsValue(toForm(customer));
        if (customer.staff) {
          setStaffOptions([
            {
              label: customer.staff.name || customer.staff.username,
              value: customer.staff.id,
            },
          ]);
        } else {
          setStaffOptions([]);
        }
        setSelectedCustomer(customer);
      },
      [form],
    );

    useImperativeHandle<any, CustomerModal>(
      ref,
      () => ({
        handleCreate() {
          form.resetFields();
          setStaffOptions([]);
          setSelectedCustomer(undefined);
          setStatus("create");
          setVisible(true);
        },
        handleUpdate(customer: Customer) {
          fillCustomer(customer);
          setStatus("update");
          setVisible(true);
        },
        handleView(customer: Customer) {
          fillCustomer(customer);
          setActiveTab("info");
          setStatus("view");
          setVisible(true);
        },
      }),
      [fillCustomer, form],
    );

    const handleClose = useCallback(() => {
      setVisible(false);
      setSelectedCustomer(undefined);
      onClose();
    }, [onClose]);

    const handleStartEdit = useCallback(() => {
      setActiveTab("info");
      setStatus("update");
    }, []);

    const handleAvatar1 = useCallback(
      (path: string) => {
        form.setFieldsValue({ contactAvatar1: path });
      },
      [form],
    );

    const handleAvatar2 = useCallback(
      (path: string) => {
        form.setFieldsValue({ contactAvatar2: path });
      },
      [form],
    );

    const buildPayload = useCallback(() => {
      const values = form.getFieldsValue();
      return {
        customer: {
          name: values.name || "",
          code: values.code || "",
          taxCode: (values.taxCode || "").trim(),
          address: values.address || "",
          contactName1: values.contactName1 || "",
          contactPhone1: values.contactPhone1 || "",
          contactEmail1: values.contactEmail1 || "",
          contactAvatar1: values.contactAvatar1 || "",
          contactPosition1: values.contactPosition1 || "",
          contactName2: values.contactName2 || "",
          contactPhone2: values.contactPhone2 || "",
          contactEmail2: values.contactEmail2 || "",
          contactAvatar2: values.contactAvatar2 || "",
          contactPosition2: values.contactPosition2 || "",
        },
        staffId: onlyMine ? undefined : values.staffId,
      };
    }, [form, onlyMine]);

    const handleSubmit = useCallback(async () => {
      await form.validateFields();
      const data = buildPayload();
      setLoading(true);
      try {
        const res =
          status === "create"
            ? await customerApi.create(data)
            : await customerApi.update(selectedCustomer?.id || 0, data);
        message.success(status === "create" ? "Tạo mới thành công!" : "Cập nhật thành công!");
        handleClose();
        onSubmitOk(res.data);
      } finally {
        setLoading(false);
      }
    }, [buildPayload, form, handleClose, onSubmitOk, selectedCustomer?.id, status]);

    const title =
      status === "create" ? "Tạo mới khách hàng" : status === "update" ? "Cập nhật khách hàng" : "Xem khách hàng";

    return (
      <Modal
        className="customerModal"
        onCancel={handleClose}
        open={visible}
        title={title}
        style={{ top: 20 }}
        width={status === "view" ? 1200 : 900}
        confirmLoading={loading}
        footer={
          status === "view" ? (
            <div className="customerModal__footer">
              <AppButton onClick={handleClose}>Đóng</AppButton>
              {canUpdate && (
                <AppButton tone="primary" onClick={handleStartEdit}>
                  Sửa
                </AppButton>
              )}
            </div>
          ) : (
            <div className="customerModal__footer">
              <AppButton onClick={handleClose}>Hủy</AppButton>
              <AppButton tone="primary" loading={loading} onClick={handleSubmit}>
                Lưu
              </AppButton>
            </div>
          )
        }
      >
        {status === "view" && selectedCustomer ? (
          <Tabs
            key={selectedCustomer.id}
            className="customerModal__tabs"
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              { key: "info", label: "Thông tin khách hàng", children: <CustomerDetail customer={selectedCustomer} /> },
              { key: "opportunity", label: "Cơ hội", children: <CustomerOpportunityTab customerId={selectedCustomer.id} /> },
              { key: "quote", label: "Báo giá", children: <CustomerQuoteTab customerId={selectedCustomer.id} /> },
              { key: "order", label: "Đơn hàng", children: <CustomerOrderTab customerId={selectedCustomer.id} /> },
              { key: "debt", label: "Công nợ", children: <CustomerDebtTab customerId={selectedCustomer.id} /> },
            ]}
          />
        ) : (
          <Form layout="vertical" form={form}>
            <div className="customerModal__title">Thông tin khách hàng</div>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Tên" name="name" rules={nameRules}>
                  <Input placeholder="" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Mã" name="code">
                  <Input placeholder="Để trống để tự sinh mã" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Mã số thuế" name="taxCode">
                  <Input placeholder="" />
                </Form.Item>
              </Col>
              <Col span={12}>
                {onlyMine ? (
                  <Form.Item label="Sale chăm sóc">
                    <Input value={userStore.info.name || ""} disabled />
                  </Form.Item>
                ) : (
                  <Form.Item label="Sale chăm sóc" name="staffId" rules={staffRules}>
                    <AppSelect
                      showSearch
                      filterOption={false}
                      options={staffOptions}
                      onSearch={debounceSearchStaff}
                      placeholder="Chọn nhân viên"
                    />
                  </Form.Item>
                )}
              </Col>
              <Col span={24}>
                <Form.Item label="Địa chỉ" name="address">
                  <Input placeholder="" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <div className="customerModal__title">Người liên hệ 1</div>
                <Form.Item label="Tên" name="contactName1">
                  <Input placeholder="" />
                </Form.Item>
                <Form.Item label="Số điện thoại" name="contactPhone1">
                  <Input placeholder="" />
                </Form.Item>
                <Form.Item label="Email" name="contactEmail1" rules={emailRules}>
                  <Input placeholder="" />
                </Form.Item>
                <Form.Item label="Chức vụ" name="contactPosition1">
                  <Input placeholder="" />
                </Form.Item>
                <Form.Item shouldUpdate>
                  {() => (
                    <Form.Item label="Avatar" name="contactAvatar1">
                      <SingleImageUpload
                        onUploadOk={handleAvatar1}
                        imageUrl={form.getFieldValue("contactAvatar1")}
                        uploadUrl={uploadUrl}
                        width={120}
                        height={120}
                      />
                    </Form.Item>
                  )}
                </Form.Item>
              </Col>
              <Col span={12}>
                <div className="customerModal__title">Người liên hệ 2</div>
                <Form.Item label="Tên" name="contactName2">
                  <Input placeholder="" />
                </Form.Item>
                <Form.Item label="Số điện thoại" name="contactPhone2">
                  <Input placeholder="" />
                </Form.Item>
                <Form.Item label="Email" name="contactEmail2" rules={emailRules}>
                  <Input placeholder="" />
                </Form.Item>
                <Form.Item label="Chức vụ" name="contactPosition2">
                  <Input placeholder="" />
                </Form.Item>
                <Form.Item shouldUpdate>
                  {() => (
                    <Form.Item label="Avatar" name="contactAvatar2">
                      <SingleImageUpload
                        onUploadOk={handleAvatar2}
                        imageUrl={form.getFieldValue("contactAvatar2")}
                        uploadUrl={uploadUrl}
                        width={120}
                        height={120}
                      />
                    </Form.Item>
                  )}
                </Form.Item>
              </Col>
            </Row>
          </Form>
        )}
      </Modal>
    );
  }),
);
